// src/pages/message-center/ShiftLeaderCommissionPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { UserRound } from 'lucide-react';
import { PageHeader, FilterPanel } from '../../components/common';
import MonthPicker from '../../components/ui/MonthPicker/MonthPicker';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getShiftLeaderCommissionForMonth } from '../../services/api/messageCenterShiftLeaderCommission';
import ShiftLeaderCommissionCard from './components/ShiftLeaderCommissionCard';
import styles from './ShiftLeaderCommissionPage.module.css';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ShiftLeaderCommissionPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();

  const [report, setReport] = useState({ isEstimate: true, grandTotal: 0, leaders: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ month: currentMonth() });

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getShiftLeaderCommissionForMonth(filters.month);
      setReport(data);
    } catch (err) {
      console.error('Error loading shift leader commission report:', err);
      showError(err.response?.data?.message || t('messageCenterShiftLeaderCommission.failedToLoadReport'));
    } finally {
      setIsLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filterDefinitions = [
    {
      key: 'month',
      label: t('messageCenterCommission.filterByMonth'),
      type: 'custom',
      render: (value, onChange) => <MonthPicker value={value} onChange={onChange} />,
    },
  ];

  return (
    <div className={styles.shiftLeaderCommissionPage}>
      <PageHeader
        title={t('messageCenterShiftLeaderCommission.viewPageTitle')}
        description={t('messageCenterShiftLeaderCommission.viewPageDescription')}
        icon={UserRound}
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={setFilters}
        onClear={() => setFilters({ month: currentMonth() })}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
      />

      {!report.disabled && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>{t('messageCenterShiftLeaderCommission.grandTotal')}</span>
            <span className={styles.summaryValue}>{formatCurrency(report.grandTotal)}</span>
          </div>
        </div>
      )}

      {isLoading && <div className={styles.loadingState}>{t('common.loading')}</div>}

      {!isLoading && report.leaders.length === 0 && (
        <div className={styles.emptyState}>
          <UserRound size={64} className={styles.emptyIcon} />
          <p>
            {report.disabled
              ? t('messageCenterShiftLeaderCommission.featureDisabled')
              : t('messageCenterShiftLeaderCommission.noLeadersForMonth')}
          </p>
        </div>
      )}

      {!isLoading && report.leaders.length > 0 && (
        <div className={styles.leaderGrid}>
          {report.leaders.map((leader) => (
            <ShiftLeaderCommissionCard key={leader.userId} leader={leader} isEstimate={report.isEstimate} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShiftLeaderCommissionPage;
